"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { t } from "@/lib/i18n/interpolate";

type Base = 2 | 8 | 10 | 16;

const BASE_DIGIT_REGEX: Record<Base, RegExp> = {
  2: /^[01]+$/,
  8: /^[0-7]+$/,
  10: /^\d+$/,
  16: /^[0-9a-fA-F]+$/,
};

const BASE_PREFIX: Record<Base, string> = {
  2: "0b",
  8: "0o",
  10: "",
  16: "0x",
};

const MAX_SAFE = BigInt(Number.MAX_SAFE_INTEGER);

type Strings = { [K in keyof typeof NUMBER_BASE_CONVERTER_STRINGS]: string };

const BASES: { value: Base; label: keyof Strings; digits: string }[] = [
  { value: 2, label: "binaryBase2", digits: "0-1" },
  { value: 8, label: "octalBase8", digits: "0-7" },
  { value: 10, label: "decimalBase10", digits: "0-9" },
  { value: 16, label: "hexadecimalBase16", digits: "0-9, A-F" },
];

function parseInBase(value: string, base: Base): bigint | null {
  if (!BASE_DIGIT_REGEX[base].test(value)) {
    return null;
  }
  return BigInt(`${BASE_PREFIX[base]}${value}`);
}

function convertValue(
  raw: string,
  base: Base,
  strings: Strings
): { value: bigint | null; error: string } {
  const cleaned = raw.trim();
  if (cleaned.length === 0) {
    return { value: null, error: "" };
  }
  const parsed = parseInBase(cleaned, base);
  if (parsed === null) {
    const digits = BASES.find((b) => b.value === base)?.digits ?? "";
    return {
      value: null,
      error: t(strings.errorInvalidNumber, {
        value: cleaned,
        base: String(base),
        digits,
      }),
    };
  }
  if (parsed > MAX_SAFE) {
    return {
      value: null,
      error: strings.errorTooLarge,
    };
  }
  return { value: parsed, error: "" };
}

function toBinarySteps(value: bigint): { line: string; remainder: string }[] {
  const steps: { line: string; remainder: string }[] = [];
  const zero = BigInt(0);
  const two = BigInt(2);
  let current = value;
  if (current === zero) {
    return [{ line: "0 ÷ 2 = 0", remainder: "0" }];
  }
  while (current > zero) {
    const quotient = current / two;
    const remainder = current % two;
    steps.push({
      line: `${current} ÷ 2 = ${quotient}`,
      remainder: remainder.toString(),
    });
    current = quotient;
  }
  return steps;
}

function ResultCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <p className="text-muted-foreground text-sm">{label}</p>
      <p className="mt-1 break-all font-mono font-semibold text-foreground text-lg">
        {value}
      </p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const NUMBER_BASE_CONVERTER_STRINGS = {
  inputBase: "Input base",
  number: "Number",
  binaryBase2: "Binary (base 2)",
  octalBase8: "Octal (base 8)",
  decimalBase10: "Decimal (base 10)",
  hexadecimalBase16: "Hexadecimal (base 16)",
  copied: "Copied!",
  copyAllBases: "Copy All Bases",
  clear: "Clear",
  how: "How ",
  convertsToBinary: " converts to binary",
  divideBy2RepeatedlyAnd:
    "Divide by 2 repeatedly and record each remainder. Reading the remainders from bottom to top gives the binary digits.",
  remaindersBottomToTop: "Remainders bottom-to-top:",
  toGoTheOtherWay:
    ". To go the other way, multiply each binary digit by its power of two and add the results to get ",
  errorInvalidNumber:
    '"{value}" is not a valid base-{base} number. Allowed digits: {digits}.',
  errorTooLarge:
    "Number is larger than 9007199254740991 (the largest safe integer this tool supports).",
  copyLineBinary: "Binary: {value}",
  copyLineOctal: "Octal: {value}",
  copyLineDecimal: "Decimal: {value}",
  copyLineHexadecimal: "Hexadecimal: {value}",
  remainderLabel: " — remainder ",
} as const;

export default function NumberBaseConverterWidget() {
  const [input, setInput] = useState("");
  const [base, setBase] = useState<Base>(10);
  const [copied, setCopied] = useState(false);

  const strings = useToolUi(NUMBER_BASE_CONVERTER_STRINGS);
  const { value, error } = convertValue(input, base, strings);
  const steps = value === null ? [] : toBinarySteps(value);

  function handleCopy() {
    if (value === null) {
      return;
    }
    const text = [
      t(strings.copyLineBinary, { value: value.toString(2) }),
      t(strings.copyLineOctal, { value: value.toString(8) }),
      t(strings.copyLineDecimal, { value: value.toString(10) }),
      t(strings.copyLineHexadecimal, {
        value: value.toString(16).toUpperCase(),
      }),
    ].join("\n");
    writeToClipboard(text).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setCopied(false);
  }

  return (
    <>
      {/* Input base */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.inputBase}
        </p>
        <div className="flex flex-wrap gap-3">
          {BASES.map((b) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={b.value}
            >
              <input
                checked={base === b.value}
                className="accent-primary"
                name="input-base"
                onChange={() => setBase(b.value)}
                type="radio"
                value={b.value}
              />
              {strings[b.label]}
            </label>
          ))}
        </div>
      </section>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="base-input"
        >
          {strings.number}
        </label>
        <input
          className="w-full rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="base-input"
          onChange={(e) => {
            setInput(e.target.value);
            setCopied(false);
          }}
          placeholder={base === 16 ? "1F4" : "500"}
          type="text"
          value={input}
        />
      </div>
      {/* Error */}
      {error.length > 0 && (
        <p className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-destructive text-sm">
          {error}
        </p>
      )}
      {/* Results */}
      {value !== null && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <ResultCard label={strings.binaryBase2} value={value.toString(2)} />
          <ResultCard label={strings.octalBase8} value={value.toString(8)} />
          <ResultCard
            label={strings.decimalBase10}
            value={value.toString(10)}
          />
          <ResultCard
            label={strings.hexadecimalBase16}
            value={value.toString(16).toUpperCase()}
          />
        </div>
      )}
      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={value === null}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyAllBases}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {/* Steps */}
      {value !== null && (
        <section className="space-y-3 rounded-lg border border-border bg-card p-6">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.how}
            {value.toString(10)}
            {strings.convertsToBinary}
          </h2>
          <p className="text-muted-foreground text-sm leading-relaxed">
            {strings.divideBy2RepeatedlyAnd}
          </p>
          <ol className="space-y-1 font-mono text-muted-foreground text-sm">
            {steps.map((step) => (
              <li key={step.line}>
                {step.line}
                {strings.remainderLabel}
                <span className="font-semibold text-foreground">
                  {step.remainder}
                </span>
              </li>
            ))}
          </ol>
          <p className="text-muted-foreground text-sm leading-relaxed">
            {strings.remaindersBottomToTop}{" "}
            <span className="break-all font-mono font-semibold text-foreground">
              {value.toString(2)}
            </span>
            {strings.toGoTheOtherWay}
            {value.toString(10)}.
          </p>
        </section>
      )}
    </>
  );
}
